import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../../components/Modal';
import { backendErrorNotification } from '../../../../../components/Notification';
import { ordersApi } from '../../../../../services/requests';
import { Order } from '../../../../../types/apiResponse/order';
import requireSuccessfulResponse from '../../../../../services/utilities/requireSuccessfulResponse';
import { ApiResPaginated } from '../../../../../types/apiResponseTypes';
import OrderDetailsModal from './index';
import { OrderDetailsTab } from './OrderDetailsContent';

export type OrderDetailsByNumberProps = {
  orderNumber: number | string;
  closeModal: () => void;
  initialTab?: OrderDetailsTab;
  onPendenciesChanged?: () => void;
};

// Resolve o pedido por numero para qualquer tela reutilizar o modal existente.
const OrderDetailsByNumberModal = ({
  orderNumber, closeModal, initialTab, onPendenciesChanged,
}: OrderDetailsByNumberProps) => {
  const { t } = useTranslation();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const version = ++generation.current;
    setLoading(true);
    setError(false);
    setOrder(null);
    try {
      const query = new URLSearchParams({
        orderNumber: String(orderNumber), withItems: 'true', withEmailLog: 'true',
        withPendencies: 'true', withDetails: 'true',
      });
      ordersApi.query = `?${query.toString()}`;
      const response = await ordersApi.index();
      requireSuccessfulResponse(response);
      const result: ApiResPaginated<Order[]> = response.data;
      if (version === generation.current) {
        if (result.data.length !== 1) {
          setError(true);
        } else {
          setOrder(result.data[0]);
        }
      }
    } catch (err) {
      if (version === generation.current) {
        setError(true);
        backendErrorNotification(err as AxiosError<any, any>);
      }
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }, [orderNumber]);

  useEffect(() => {
    void load();
    return () => { generation.current += 1; };
  }, [load]);

  if (order) return (
    <OrderDetailsModal showModal closeModal={closeModal} order={order}
      initialTab={initialTab} onPendenciesChanged={onPendenciesChanged} />
  );

  return (
    <Modal showModal onlyCloseButton title={`${t('orderDetails')}: ${orderNumber}`} closeModal={closeModal}>
      {loading && <p role="status">{t('pendencyOrderLoading')}</p>}
      {error && <><p>{t('pendencyOrderUnavailable')}</p><button onClick={() => void load()}>{t('pendencyRetry')}</button></>}
    </Modal>
  );
};

export default OrderDetailsByNumberModal;
